import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';

import { ProductsApiService } from '../api/products-api.service';
import type { CreateProductRequest, ProductDto, ProductListResponse, UpdateProductRequest } from '../models/product.model';

@Injectable({ providedIn: 'root' })
export class ProductsStore {
  private readonly api = inject(ProductsApiService);

  readonly products = signal<ProductDto[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly meta = signal<ProductListResponse['meta'] | null>(null);

  load(params?: { page?: number; q?: string }): void {
    this.loading.set(true);
    this.error.set(null);

    this.api
      .list(params)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.products.set(response.data);
          this.meta.set(response.meta);
        },
        error: (error: unknown) => {
          this.error.set(resolveErrorMessage(error));
        }
      });
  }

  create(body: CreateProductRequest): Observable<ProductDto> {
    this.error.set(null);

    return this.api.create(body).pipe(
      map((response) => response.data),
      tap((product) => {
        this.products.update((current) => [product, ...current]);
      }),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  update(id: string, body: UpdateProductRequest): Observable<ProductDto> {
    this.error.set(null);

    return this.api.update(id, body).pipe(
      map((response) => response.data),
      tap((product) => this.upsert(product)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  toggleStatus(id: string, isActive?: boolean): void {
    this.error.set(null);

    this.api.toggleStatus(id, isActive).subscribe({
      next: (response) => this.upsert(response.data),
      error: (error: unknown) => {
        this.error.set(resolveErrorMessage(error));
      }
    });
  }

  delete(id: string): Observable<{ data: { deleted: true } }> {
    this.error.set(null);

    return this.api.delete(id).pipe(
      tap(() => {
        this.products.update((current) => current.filter((product) => product.id !== id));
      }),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  private upsert(product: ProductDto): void {
    this.products.update((current) => {
      const index = current.findIndex((item) => item.id === product.id);

      if (index === -1) {
        return [product, ...current];
      }

      return current.map((item) => (item.id === product.id ? product : item));
    });
  }
}

const resolveErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'No pudimos completar la operación sobre productos.';
};
